import * as Sentry from '@sentry/react';
import axios from 'axios';
import mixpanel from 'mixpanel-browser';
import moment from 'moment';
import 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { setBnlPreShoot } from '../../../actions/action_bnl_pre_shoot';
import { skipYearbookSelectionAndGoToOrder } from '../../../actions/yearbook';
import {
  formatAndSetBackgroundOptions,
  formatAndSetIncentiveProducts,
  getShootData,
  setInterstitialProducts,
  setPersonalizationProduct,
  setPreCartOfferProducts,
  setRetouchingProduct,
  setRetouchingProducts,
  setTriggersPreCartOfferProducts,
} from '../../../actions/action_shoot';
import { setAdditionalData } from '../../../actions/additionalData';
import cartUtils from '../../../utils/cart';
import {
  EditPoseLink,
  EventWrapper,
  GalleryImage,
  GalleryImageContainer,
  GalleryImageContainerWrapper,
  GalleryImageName,
  GalleryOrderButton,
  GalleryWrapper,
  GroupGalleryImage,
  SelectPoseBanner,
  SelectedBanner,
  StudentCard,
  StudentName,
  ViewGalleryButton,
} from './dashboardGalleriesStyles';

const DashboardGalleries = ({ history }) => {
  // TODO: move to a const file?
  const BASE_IMAGE_URL = `https://${process.env.IMAGE_PREVIEW_BUCKET}.s3.amazonaws.com/`;
  const BASE_WATERMARKED_IMAGE_URL = `https://${process.env.IMAGE_PREVIEW_WATERMARKED_BUCKET}.s3.amazonaws.com/`;

  const dispatch = useDispatch();

  const orders = useSelector((state) => state.v3Order.orders);
  const skipYearbookSelection = useSelector(
    (state) => state.yearbookPoses?.skipSelectionForSession,
  );
  const currentUserId = useSelector((state) => state.auth.currentUserId);

  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dataIsLoading, setDataIsLoading] = useState(false);

  useEffect(() => {
    if (currentUserId) {
      mixpanel.identify(currentUserId);

      // Optionally, you might also want to set profile properties here
      mixpanel.people.set({
        // Set any relevant user properties
      });
    }
  }, [currentUserId]);

  useEffect(() => {
    let isMounted = true; // Flag to track mounted status

    const fetchData = async () => {
      try {
        setIsLoading(true); // Start loading
        const response = await axios.get('/api/v2/dashboard');

        if (isMounted) {
          setDashboardData(response.data); // Only update state if component is mounted
          setIsLoading(false); // Only update state if component is mounted
        }
      } catch (error) {
        // console.error('Failed to fetch galleries: ', error);
        if (isMounted) {
          setIsLoading(false); // Only update state if component is mounted
        }
      }
    };

    fetchData();

    // Cleanup function to set isMounted to false when component unmounts
    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array to run the effect only once
  const renderBanner = (student) => {
    const gallery = student.galleries.find(
      (gallery) => gallery.requires_yearbook_selection,
    );
    return (
      <SelectPoseBanner>
        {`Important: please select a yearbook pose by ${moment(
          gallery.yearbook_selection_deadline,
        ).format('MMMM DD, YYYY')}`}
        <ViewGalleryButton
          onClick={() => {
            dispatch(skipYearbookSelectionAndGoToOrder(false)).then(() => {
              history.push(`/v3/yearbook-selection/${student.id}`);
            });
          }}
        >
          View Gallery
        </ViewGalleryButton>
      </SelectPoseBanner>
    );
  };

  const handleImageClick = (student, gallery) => () => {
    if (
      gallery.requires_yearbook_selection &&
      !gallery.yearbook_selection_performed &&
      isBeforeDeadline(
        gallery.yearbook_selection_deadline,
        gallery.studio_time_zone,
      )
    ) {
      goToOrder(student, gallery);
    }
  };

  const handleEditPoseClick = (student, gallery) => () => {
    goToOrder(student, gallery, true);
  };
  // TODO: move to a helper?
  const formatBackgroundOptions = (
    containsBackgroundCategories,
    backgroundOptions,
  ) => {
    const formattedBackgroundOptions = {};

    if (containsBackgroundCategories) {
      backgroundOptions.forEach((backgroundSet) => {
        backgroundSet.background_images.forEach((backgroundOption) => {
          formattedBackgroundOptions[backgroundOption.id] = backgroundOption;
        });
      });
    } else {
      backgroundOptions.forEach((backgroundOption) => {
        formattedBackgroundOptions[backgroundOption.id] = backgroundOption;
      });
    }

    dispatch(formatAndSetBackgroundOptions(formattedBackgroundOptions));
  };

  // TODO: move to a helper?
  const formatIncentiveProducts = (incentiveProducts) => {
    const formattedIncentiveProducts = {};

    incentiveProducts.forEach((incentiveProduct) => {
      if (!formattedIncentiveProducts[incentiveProduct.incentive_threshold]) {
        formattedIncentiveProducts[incentiveProduct.incentive_threshold] = [];
      }

      formattedIncentiveProducts[incentiveProduct.incentive_threshold].push(
        incentiveProduct,
      );
    });

    dispatch(formatAndSetIncentiveProducts(formattedIncentiveProducts));
  };

  // TODO: can we remove this? i.e. is it needed in v3?
  // const setUnlockedGroups = (studentId) => {
  //   const orderState = this.props.cart.orders[studentId];
  //   let bnlUnlockedGroups = [];

  //   if (orderState) {
  //     bnlUnlockedGroups = this.cloneArray(this.props.cart.bnlUnlockedGroups);

  //     orderState.cartItems.forEach((cartItem) => {
  //       if (cartItem.lock_type === 'unlocker') {
  //         cartItem.lock_group.forEach((set) => {
  //           if (!bnlUnlockedGroups.includes(set)) bnlUnlockedGroups.push(set);
  //         });
  //       }
  //     });
  //   }

  //   dispatch(updateBnlUnlockedGroups(bnlUnlockedGroups));
  // };

  const determinePriceListIdForOrder = (gallery) => {
    // Check if still in prepay period (open for preorders)
    if (gallery.is_open_for_prepay) {
      // console.log('Gallery is in prepay period, using default pricing model');
      return gallery.company_pricing_model_id;
    }

    // After prepay period: Check reorder models first (takes precedence over post-shoot)
    if (
      gallery.reorder_buyer_pricing_model_id &&
      gallery.reorder_non_buyer_pricing_model_id
    ) {
      if (gallery.gallery_buyer_status_at_ingest === 'buyer') {
        // console.log('Using reorder buyer pricing model');
        return gallery.reorder_buyer_pricing_model_id;
      } else {
        // Both 'non_buyer' and 'unknown' default to non_buyer pricing
        // console.log(
        //   `Using reorder non-buyer pricing model (status: ${gallery.gallery_buyer_status_at_ingest})`,
        // );
        return gallery.reorder_non_buyer_pricing_model_id;
      }
    }

    // Then check post-shoot pricing model
    if (gallery.post_shoot_company_pricing_model_id) {
      // console.log('Using post-shoot pricing model');
      return gallery.post_shoot_company_pricing_model_id;
    }

    // Finally fall back to default company pricing model
    // console.log('Using default company pricing model');
    return gallery.company_pricing_model_id;
  };

  const isBeforeDeadline = (deadline, studioTimeZone) => {
    const nowInStudioTZ = moment().tz(studioTimeZone);
    const deadlineInStudioTZ = moment
      .tz(deadline, 'YYYY-MM-DD', studioTimeZone)
      .endOf('day');

    return nowInStudioTZ.isBefore(deadlineInStudioTZ);
  };

  const shouldShowBanner = (student) => {
    return student.galleries.some(
      (gallery) =>
        gallery.requires_yearbook_selection &&
        !gallery.yearbook_selection_performed &&
        isBeforeDeadline(
          gallery.yearbook_selection_deadline,
          gallery.studio_time_zone,
        ),
    );
  };

  const goToOrder = (student, gallery, isEdit = false) => {
    // console.log('👉 GO TO ORDER');
    // console.log('student: ', student);
    // console.log('gallery: ', gallery);
    // console.log('isEdit: ', isEdit);

    const requiresYearbookSelection = gallery.requires_yearbook_selection;
    const studioTimeZone = gallery.studio_time_zone;
    const yearbookSelectionDeadline = gallery.yearbook_selection_deadline;

    const hasYearbookSelection = gallery.yearbook_selection_performed;

    // console.log('requiresYearbookSelection: ', requiresYearbookSelection);

    const selectedGalleryId = gallery.id;

    const showPriceListMatchedPoses =
      dashboardData?.show_price_list_matched_poses_in_order_ui || false;

    // console.log('showPriceListMatchedPoses: ', showPriceListMatchedPoses);

    const v3CartContainsPrepayOrders =
      cartUtils.cartContainsPrepayOrders(orders);

    if (v3CartContainsPrepayOrders) {
      Swal.fire({
        title: 'Your cart contains prepay orders',
        html: '<p>Your cart contains prepay orders, which cannot be submitted with gallery-based orders.</p><p>Please submit or clear your prepay orders to enable gallery-based ordering.</p>',
        icon: 'warning',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Okay',
      });

      Sentry.setContext('orders', {
        orders: orders,
        orderKeys: Object.keys(orders),
      });

      Sentry.captureMessage('pre-order in progress warning');

      return;
    }

    const priceListIdForOrder = determinePriceListIdForOrder(gallery);

    // TODO: This was a super quick fix for SSI events that don't have
    // a default price list. Hopefully this error isn't important for any
    // other reason, and users really shouldn't encounter it anyway if
    // events have price lists.
    //if (!priceListIdForOrder) {
    //  Swal.fire({
    //    title: 'Error',
    //    html: '<p>Something went wrong. Please try again in a few minutes.</p>',
    //    icon: 'warning',
    //    confirmButtonColor: '#3085d6',
    //    confirmButtonText: 'Okay',
    //  });

    //  Sentry.captureMessage(
    //    `Cannot determine price list for gallery ${gallery.id}`,
    //  );

    //  return;
    //}

    const galleryThatInitiatedOrder = {
      galleryId: gallery.id,
      studentId: student.id,
      buyerStatusAtIngest: gallery.gallery_buyer_status_at_ingest,
      priceListId: gallery.company_pricing_model_id,
      postEventPriceListId: gallery.post_shoot_company_pricing_model_id,
      reorderBuyerPriceListId: gallery.reorder_buyer_pricing_model_id,
      reorderNonBuyerPriceListId: gallery.reorder_non_buyer_pricing_model_id,
      priceListIdForOrder,
    };

    const selectedGalleryImages = [];
    const otherGalleryImages = [];

    dashboardData.students.forEach((student) => {
      student.galleries.forEach((tmpGallery) => {
        // console.log('Checking gallery:', {
        //   galleryId: tmpGallery.id,
        //   buyerStatus: tmpGallery.gallery_buyer_status_at_ingest,
        //   companyPricingModelId: tmpGallery.company_pricing_model_id,
        //   postShootModelId: tmpGallery.post_shoot_company_pricing_model_id,
        //   reorderBuyerId: tmpGallery.reorder_buyer_pricing_model_id,
        //   reorderNonBuyerId: tmpGallery.reorder_non_buyer_pricing_model_id,
        //   isOpenForPrepay: tmpGallery.is_open_for_prepay,
        //   priceListIdForOrder,
        // });

        // console.log('priceListIdForOrder: ', priceListIdForOrder);

        // Make the price list match logic more readable by breaking it out
        const hasCompanyPricingModelMatch =
          tmpGallery.company_pricing_model_id === priceListIdForOrder;

        const hasPostShootModelMatch =
          tmpGallery.post_shoot_company_pricing_model_id ===
          priceListIdForOrder;

        const hasReorderNonBuyerMatch =
          tmpGallery.reorder_non_buyer_pricing_model_id === priceListIdForOrder;

        const hasReorderBuyerMatch =
          tmpGallery.reorder_buyer_pricing_model_id === priceListIdForOrder;

        // console.log(
        //   'hasCompanyPricingModelMatch: ',
        //   hasCompanyPricingModelMatch,
        // );
        // console.log('hasPostShootModelMatch: ', hasPostShootModelMatch);
        // console.log('hasReorderNonBuyerMatch: ', hasReorderNonBuyerMatch);
        // console.log('hasReorderBuyerMatch: ', hasReorderBuyerMatch);

        // Only consider a match if priceListIdForOrder is not null
        const isPriceListMatch =
          priceListIdForOrder != null &&
          (hasCompanyPricingModelMatch ||
            hasPostShootModelMatch ||
            hasReorderNonBuyerMatch ||
            hasReorderBuyerMatch);

        if (isPriceListMatch) {
          // add the gallery that initiated the order to the selectedGalleryImages array
          if (tmpGallery.id === selectedGalleryId) {
            // console.log('Adding gallery to selectedGalleryImages');
            selectedGalleryImages.push(...tmpGallery.images);
          } else {
            // only add other galleries if showPriceListMatchedPoses is true
            if (showPriceListMatchedPoses) {
              // console.log('Adding gallery to otherGalleryImages');
              otherGalleryImages.push(...tmpGallery.images);
            }
          }
        }
      });
    });

    const allAvailablePosesForOrder = [
      ...selectedGalleryImages,
      ...otherGalleryImages,
    ];

    // console.log('galleryThatInitiatedOrder');
    // console.log(galleryThatInitiatedOrder);

    const data = {
      student_id: student.id,
      shoot_key: gallery?.password || gallery?.key,
      event_id: gallery?.event_id,
      grade_code: 'IDK',
    };

    setDataIsLoading(true);

    dispatch(setBnlPreShoot(false));

    dispatch(getShootData(data)).then((resp) => {
      if (!resp.payload.data.errors) {
        const shootData = resp.payload.data;

        // TODO: determine why we need this formatting logic... can we just
        // return the backgrounds and incentive products formatted as needed?
        formatBackgroundOptions(
          shootData.contains_background_categories,
          shootData.background_options,
        );
        formatIncentiveProducts(shootData.incentive_products);

        // TODO: can we remove this? i.e. is it needed in v3?
        // setUnlockedGroups(student.id);

        dispatch(setRetouchingProduct(shootData.retouching_product));
        dispatch(setRetouchingProducts(shootData.retouching_products));
        dispatch(setPersonalizationProduct(shootData.personalization_product));
        dispatch(setInterstitialProducts(shootData.interstitial_products));
        dispatch(setPreCartOfferProducts(shootData.pre_cart_offer_products));
        dispatch(
          setTriggersPreCartOfferProducts(
            shootData.triggers_pre_cart_offer_products,
          ),
        );

        // TODO: do we need to set this for v3?
        // that.props.setCpmId(cpmId);

        // Derive student preview image URL from first gallery image
        let studentPreviewImageUrl = null;
        if (gallery?.images?.[0]) {
          const firstImage = gallery.images[0];
          if (firstImage.watermarked_preview_filename) {
            studentPreviewImageUrl = `${BASE_WATERMARKED_IMAGE_URL}${firstImage.watermarked_preview_filename}`;
          } else if (firstImage.url) {
            studentPreviewImageUrl = firstImage.url;
          } else if (firstImage.web_preview_filename) {
            studentPreviewImageUrl = `${BASE_IMAGE_URL}${firstImage.web_preview_filename}`;
          }
        }

        dispatch(
          setAdditionalData({
            // availablePoses: studentGalleryObj.gallery,
            allAvailablePosesForOrder,
            availablePoses: gallery?.images || [],
            availableGroupImages: gallery?.associated_group_images || [],
            galleryThatInitiatedOrder,
            picsGalleryId: gallery?.pics_gallery_id,
            studentGallery: gallery?.images ? gallery?.images : [],
            studentGroupImages: gallery?.associated_group_images || [],
            selectedStudent: student,
            student,
            studentId: student.id,
            studentPreviewImageUrl,
            hideSelectedPosesInOrderUi:
              dashboardData?.hide_selected_poses_in_order_ui || false,
          }),
        );

        setDataIsLoading(false);

        if (
          isEdit ||
          (requiresYearbookSelection &&
            !hasYearbookSelection &&
            !skipYearbookSelection)
        ) {
          const nowInStudioTZ = moment().tz(studioTimeZone);
          const deadline = moment
            .tz(yearbookSelectionDeadline, 'YYYY-MM-DD', studioTimeZone)
            .endOf('day');

          if (!nowInStudioTZ.isAfter(deadline)) {
            return history.push(`/v3/yearbook-selection/${student.id}`);
          }
        }

        history.push('/v3/incentive-offers');
      }
    });
  };

  return (
    <>
      <div className={dataIsLoading ? 'loading' : 'hidden'}>
        <div className="spinner" />
      </div>
      {!dashboardData && isLoading && (
        <div>
          <p>loading...</p>
        </div>
      )}
      {dashboardData && !isLoading && (
        <div>
          {dashboardData.students.map((student) => {
            return (
              <StudentCard key={student.id}>
                {shouldShowBanner(student) && renderBanner(student)}
                <StudentName>
                  {student.first_name} {student.last_name}
                </StudentName>
                {student.galleries.map((gallery) => {
                  if (
                    !Array.isArray(gallery.images) ||
                    gallery.images.length === 0
                  ) {
                    return null;
                  }

                  return (
                    <EventWrapper key={gallery.id}>
                      <div key={gallery.event_name}>
                        <strong>{gallery.event_name}</strong>
                        {gallery.shoot_date && (
                          <div style={{ color: '#888' }}>
                            Date:{' '}
                            {moment(gallery.shoot_date).format('MMMM D, YYYY')}
                          </div>
                        )}
                        <div>{gallery.gallery_job_created_at_year}</div>
                        <GalleryOrderButton
                          className="order-from-gallery-button"
                          onClick={() => {
                            goToOrder(student, gallery);
                          }}
                        >
                          order from this gallery
                        </GalleryOrderButton>
                        {gallery.yearbook_selection_performed &&
                          isBeforeDeadline(
                            gallery.yearbook_selection_deadline,
                            gallery.studio_time_zone,
                          ) && (
                            <EditPoseLink
                              onClick={handleEditPoseClick(student, gallery)}
                            >
                              Edit yearbook pose selection
                            </EditPoseLink>
                          )}
                        <GalleryWrapper>
                          {gallery.images.map((image) => {
                            // If image has cropped_images array with content, render those
                            if (
                              image.cropped_images &&
                              image.cropped_images.length > 0
                            ) {
                              return image.cropped_images.map((cropImage) => {
                                // Determine image source for crop image
                                let cropImageSrc = '';
                                if (
                                  cropImage.watermarked_preview_filename ===
                                    undefined &&
                                  cropImage.url !== null &&
                                  cropImage.url !== undefined &&
                                  cropImage.url !== ''
                                ) {
                                  cropImageSrc = cropImage.url;
                                } else {
                                  cropImageSrc =
                                    cropImage.watermarked_preview_filename !==
                                      null &&
                                    cropImage.watermarked_preview_filename !==
                                      undefined
                                      ? `${BASE_WATERMARKED_IMAGE_URL}${cropImage.watermarked_preview_filename}`
                                      : `${BASE_IMAGE_URL}${cropImage.web_preview_filename}`;
                                }

                                return (
                                  <GalleryImageContainerWrapper
                                    key={
                                      cropImage.web_preview_filename ||
                                      cropImage.id
                                    }
                                  >
                                    <GalleryImageContainer>
                                      <GalleryImage
                                        onContextMenu={(event) =>
                                          event.preventDefault()
                                        }
                                        onClick={handleImageClick(
                                          student,
                                          gallery,
                                        )}
                                        src={cropImageSrc}
                                        alt="Gallery Image Preview"
                                      />
                                      {cropImage.yearbook_selection && (
                                        <SelectedBanner>
                                          ✅ Selected Pose
                                        </SelectedBanner>
                                      )}
                                    </GalleryImageContainer>
                                    <GalleryImageName>
                                      {cropImage.name || image.name}
                                    </GalleryImageName>
                                  </GalleryImageContainerWrapper>
                                );
                              });
                            }

                            // Otherwise, render the original image (existing code)
                            // TODO: revisit this logic
                            let imageSrc = '';

                            if (
                              image.watermarked_preview_filename ===
                                undefined &&
                              image.url !== null &&
                              image.url !== undefined &&
                              image.url !== ''
                            ) {
                              imageSrc = image.url;
                            } else {
                              imageSrc =
                                image.watermarked_preview_filename !== null &&
                                image.watermarked_preview_filename !== undefined
                                  ? `${BASE_WATERMARKED_IMAGE_URL}${image.watermarked_preview_filename}`
                                  : `${BASE_IMAGE_URL}${image.web_preview_filename}`;
                            }

                            return (
                              <GalleryImageContainerWrapper
                                key={image.web_preview_filename || image.id}
                              >
                                <GalleryImageContainer>
                                  <GalleryImage
                                    onContextMenu={(event) =>
                                      event.preventDefault()
                                    }
                                    onClick={handleImageClick(student, gallery)}
                                    src={imageSrc}
                                    alt="Gallery Image Preview"
                                  />
                                  {image.yearbook_selection && (
                                    <SelectedBanner>
                                      ✅ Selected Pose
                                    </SelectedBanner>
                                  )}
                                </GalleryImageContainer>
                                <GalleryImageName>
                                  {image.name}
                                </GalleryImageName>
                              </GalleryImageContainerWrapper>
                            );
                          })}
                          {gallery.associated_group_images.map((image) => {
                            let imageSrc = '';

                            if (
                              image.watermarked_preview_filename ===
                                undefined &&
                              image.url !== null &&
                              image.url !== undefined &&
                              image.url !== ''
                            ) {
                              imageSrc = image.url;
                            } else {
                              imageSrc =
                                image.watermarked_preview_filename !== null &&
                                image.watermarked_preview_filename !== undefined
                                  ? `${BASE_WATERMARKED_IMAGE_URL}${image.watermarked_preview_filename}`
                                  : `${BASE_IMAGE_URL}${image.web_preview_filename}`;
                            }

                            return (
                              <GalleryImageContainer>
                                <GroupGalleryImage
                                  onContextMenu={(event) =>
                                    event.preventDefault()
                                  }
                                  key={image.web_preview_filename}
                                  src={imageSrc}
                                  alt="Gallery Image Preview"
                                />
                                {image.yearbook_selection && (
                                  <SelectedBanner>
                                    ✅ Selected Pose
                                  </SelectedBanner>
                                )}
                              </GalleryImageContainer>
                            );
                          })}
                        </GalleryWrapper>
                      </div>
                    </EventWrapper>
                  );
                })}
              </StudentCard>
            );
          })}
        </div>
      )}
    </>
  );
};

export default DashboardGalleries;
